import axios from "axios";

// =========================================================
// CONFIGURATION
// =========================================================

const API_ORIGIN =
  process.env.REACT_APP_ROHAN_API_ORIGIN || "https://rohanbackend.axiomos.in";

const LATEST_KEY_ACCESS_TOKEN =
  process.env.REACT_APP_LATEST_KEY_ACCESS_TOKEN ||
  "t9aZI42xdKRz3q7C3ooEU3fmX8v";

  const rohanAxios = axios.create({
    baseURL: API_ORIGIN,
    timeout: 30000,
    headers: {
      Accept: "application/json",
    },
  });

/*
 * Separate Axios instance used only for retrieving the
 * current rotating key.
 *
 * It does not inherit interceptors installed on the
 * default Axios instance.
 */
const keyAxios = axios.create({
  timeout: 20000,
  headers: {
    Accept: "application/json",
  },
});

let pendingKeyRequest = null;
let interceptorInstalled = false;

// =========================================================
// CLEAN OLD CLIENT STORAGE
// =========================================================

if (typeof window !== "undefined") {
  window.localStorage.removeItem("rohanLatestRotatingApiKey");
  window.localStorage.removeItem("latestApiKey");
}

// =========================================================
// URL HELPERS
// =========================================================

const getRohanOrigin = () => {
  try {
    return new URL(API_ORIGIN).origin;
  } catch (error) {
    console.error("Invalid REACT_APP_ROHAN_API_ORIGIN:", API_ORIGIN, error);

    return "";
  }
};

/**
 * Resolve an Axios config into a complete URL.
 *
 * Supported:
 * - https://rohanbackend.axiomos.in/projects
 * - /projects
 * - relative URL with baseURL
 */
const resolveRequestUrl = (config = {}) => {
  try {
    const requestUrl = String(config?.url || "").trim();

    if (!requestUrl) {
      return null;
    }

    if (/^https?:\/\//i.test(requestUrl)) {
      return new URL(requestUrl);
    }

    if (config?.baseURL) {
      return new URL(requestUrl, config.baseURL);
    }

    if (requestUrl.startsWith("/")) {
      return new URL(requestUrl, API_ORIGIN);
    }

    return null;
  } catch (error) {
    console.error("Unable to resolve API request URL:", {
      url: config?.url,
      baseURL: config?.baseURL,
      error,
    });

    return null;
  }
};

const getFirstPathSegment = (pathname = "") => {
  return String(pathname)
    .split("/")
    .find((segment) => segment.trim() !== "");
};

/**
 * The latestKey endpoint already has its permanent token.
 */
const isExcludedRoute = (pathname = "") => {
  const firstSegment = getFirstPathSegment(pathname);

  if (!firstSegment) {
    return true;
  }

  const routeName = firstSegment.split(":")[0];

  return routeName === "latestKey";
};

const isRohanBackendRequest = (config = {}) => {
  if (config?.skipRotatingApiKey === true) {
    return false;
  }

  const resolvedUrl = resolveRequestUrl(config);

  if (!resolvedUrl) {
    return false;
  }

  const rohanOrigin = getRohanOrigin();

  if (!rohanOrigin) {
    return false;
  }

  return (
    resolvedUrl.origin === rohanOrigin && !isExcludedRoute(resolvedUrl.pathname)
  );
};

// =========================================================
// ROTATING KEY URL HANDLING
// =========================================================

const KEY_PATTERN = "[A-Za-z0-9_-]+";

/**
 * Remove an existing key from the end of the pathname.
 *
 * /projects:OLD_KEY
 * becomes:
 * /projects
 *
 * /meeting/count:OLD_KEY?project=123
 * becomes:
 * /meeting/count?project=123
 */
const removeExistingRotatingKey = (completeUrl = "") => {
  const rawUrl = String(completeUrl || "").trim();

  if (!rawUrl) {
    return "";
  }

  try {
    const parsedUrl = new URL(rawUrl, API_ORIGIN);

    parsedUrl.pathname = parsedUrl.pathname.replace(
      new RegExp(`:${KEY_PATTERN}$`),
      "",
    );

    /*
     * Also repair URLs created by the previous incorrect
     * implementation:
     *
     * /meeting/count?project=123:OLD_KEY
     */
    parsedUrl.search = parsedUrl.search.replace(
      new RegExp(`:${KEY_PATTERN}$`),
      "",
    );

    return parsedUrl.toString();
  } catch (error) {
    /*
     * Fallback for malformed or relative strings.
     */
    const [pathAndQuery, hash = ""] = rawUrl.split("#");

    const [pathname, queryString] = pathAndQuery.split("?");

    const cleanPathname = pathname.replace(new RegExp(`:${KEY_PATTERN}$`), "");

    const cleanQuery = queryString
      ? queryString.replace(new RegExp(`:${KEY_PATTERN}$`), "")
      : "";

    const queryPart = cleanQuery ? `?${cleanQuery}` : "";
    const hashPart = hash ? `#${hash}` : "";

    return `${cleanPathname}${queryPart}${hashPart}`;
  }
};

/**
 * Append the rotating key to the end of the PATHNAME,
 * immediately before the query string.
 *
 * Correct backend formats:
 *
 * /projects:KEY
 * /user/login:KEY
 * /meeting/RECORD_ID:KEY
 * /meeting/count:KEY?project=123
 * /uauc/count:KEY?status=Open&project=123
 */
const injectKeyBeforeQuery = (completeUrl, key) => {
  const cleanUrl = removeExistingRotatingKey(completeUrl);
  const normalizedKey = String(key || "").trim();

  if (!cleanUrl) {
    return "";
  }

  if (!normalizedKey) {
    return cleanUrl;
  }

  try {
    const parsedUrl = new URL(cleanUrl, API_ORIGIN);

    parsedUrl.hash = "";

    parsedUrl.pathname =
      parsedUrl.pathname.replace(/\/+$/, "") +
      `:${encodeURIComponent(normalizedKey)}`;

    return parsedUrl.toString();
  } catch (error) {
    const [pathAndQuery] = cleanUrl.split("#");
    const queryIndex = pathAndQuery.indexOf("?");

    if (queryIndex === -1) {
      return `${pathAndQuery}:${encodeURIComponent(normalizedKey)}`;
    }

    const pathname = pathAndQuery.slice(0, queryIndex);
    const queryString = pathAndQuery.slice(queryIndex);

    return `${pathname}:${encodeURIComponent(normalizedKey)}` + queryString;
  }
};

/**
 * Backward-compatible export name.
 *
 * Existing imports of injectKeyAtAbsoluteEnd will continue
 * working, but the implementation now correctly inserts the
 * key before the query string.
 */
const injectKeyAtAbsoluteEnd = injectKeyBeforeQuery;

/**
 * Apply rotating key to an Axios request.
 */
const applyKeyToRequest = (config, key) => {
  const resolvedUrl = resolveRequestUrl(config);

  if (!resolvedUrl) {
    return config;
  }

  /*
   * Hash fragments are not sent to the server.
   */
  resolvedUrl.hash = "";

  const protectedUrl = injectKeyBeforeQuery(resolvedUrl.toString(), key);

  return {
    ...config,

    /*
     * URL is now absolute, so baseURL must not be applied
     * again.
     */
    baseURL: undefined,
    url: protectedUrl,
  };
};

// =========================================================
// FETCH CURRENT ROTATING KEY
// =========================================================

const fetchLatestApiKey = async () => {
  if (pendingKeyRequest) {
    return pendingKeyRequest;
  }

  pendingKeyRequest = (async () => {
    const latestKeyUrl =
      `${API_ORIGIN}/latestKey:` + encodeURIComponent(LATEST_KEY_ACCESS_TOKEN);

    const response = await keyAxios.get(latestKeyUrl, {
      headers: {
        Accept: "application/json",
      },

      params: {
        _ts: Date.now(),
      },
    });

    const latestKey = String(response?.data?.key || "").trim();

    if (!latestKey) {
      throw new Error(
        response?.data?.message ||
          "Latest-key endpoint returned an invalid key.",
      );
    }

    return latestKey;
  })();

  try {
    return await pendingKeyRequest;
  } finally {
    pendingKeyRequest = null;
  }
};

// =========================================================
// INSTALL AXIOS INTERCEPTORS
// =========================================================

const installRotatingApiKeyInterceptor = () => {
  if (interceptorInstalled) {
    return;
  }

  interceptorInstalled = true;

  axios.interceptors.request.use(
    async (config) => {
      if (!isRohanBackendRequest(config)) {
        return config;
      }

      const latestKey = await fetchLatestApiKey();

      const protectedConfig = applyKeyToRequest(config, latestKey);

      if (process.env.NODE_ENV === "development") {
        console.log("Protected Rohan API request:", {
          originalUrl: config?.url,
          finalUrl: protectedConfig?.url,
          method: config?.method,
        });
      }

      return protectedConfig;
    },

    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    (response) => response,

    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;

      const canRetry =
        Boolean(originalRequest) &&
        originalRequest.__rotatingKeyRetry !== true &&
        isRohanBackendRequest(originalRequest) &&
        (status === 401 || status === 403);

      if (!canRetry) {
        return Promise.reject(error);
      }

      originalRequest.__rotatingKeyRetry = true;

      try {
        const latestKey = await fetchLatestApiKey();

        const retryRequest = applyKeyToRequest(
          {
            ...originalRequest,

            /*
             * Key is manually applied to the retry.
             */
            skipRotatingApiKey: true,
          },
          latestKey,
        );

        if (process.env.NODE_ENV === "development") {
          console.log("Retrying Rohan API request:", {
            status,
            finalUrl: retryRequest?.url,
            method: retryRequest?.method,
          });
        }

        return axios.request(retryRequest);
      } catch (keyError) {
        console.error("Unable to retrieve a valid rotating API key:", keyError);

        return Promise.reject(error);
      }
    },
  );
};

installRotatingApiKeyInterceptor();

// =========================================================
// EXPORTS
// =========================================================

export {
  API_ORIGIN,
  fetchLatestApiKey,
  installRotatingApiKeyInterceptor,
  removeExistingRotatingKey,
  injectKeyBeforeQuery,
  injectKeyAtAbsoluteEnd,
  applyKeyToRequest,
};

export default axios;
